import { Calendar } from 'lucide-react';
import { useMemo } from 'react';

import Box from '@/common/components/Box';
import { normalizeText } from '@/common/utils/formatters';
import { Mapper } from '@/common/services/mapper';
import { useMissionProducts } from '../hooks/useMissionProducts';
import { useMissionProviders } from '../hooks/useMissionProviders';
import { MissionProvidersTransformer } from '../services/missionProvidersTransformer';
import { MissionDetailsTransformer } from '../services/missionDetailsTransformer';
import { OPTIONAL_QUANTITY_LABELS } from '../constants/optionalQuantityLabels.constant';
import {
  Mission,
  MissionCondition,
  MissionReward,
  MissionRewardType,
} from "../types/mission.type";
import { CardField, CardItem, ReadOnlyToggle, ViewField } from "./MissionDetailsView/components";
import { MissionProvider } from '../types/missionProvider.type';
import { missionTypeStrategy } from '@/features/missions/constants/missionOptions.constant';

interface MissionDetailsViewProps {
  mission: Mission;
}

export const MissionDetailsView = ({ mission }: MissionDetailsViewProps) => {
  const { data: products = [], dataUpdatedAt: productsUpdatedAt } = useMissionProducts({});
  const {
    data: providers = [],
    dataUpdatedAt: providersUpdatedAt,
  } = useMissionProviders<MissionProvider[]>({
    mapper: Mapper.mapWithTransformer({
      transformer: MissionProvidersTransformer.normalizeProviders,
    }),
  });

  const productDictionary = useMemo(() => {
    return products.reduce<Record<string, string>>((acc, product) => {
      acc[String(product.id)] = product.display_name || `Produto ${product.id}`;
      return acc;
    }, {});
  }, [productsUpdatedAt]);

  const providerDictionary = useMemo<Record<string, string>>(() => {
    const providerList: MissionProvider[] = Array.isArray(providers)
      ? (providers as MissionProvider[])
      : [];

    return providerList.reduce<Record<string, string>>(
      (acc: Record<string, string>, provider: MissionProvider) => {
        const key = provider.provider_klass;
        if (!key) {
          return acc;
        }

        acc[key] =
          provider.display_name ||
          MissionProvidersTransformer.humanizeProviderKlass(key) ||
          key;
        return acc;
      },
      {},
    );
  }, [providersUpdatedAt]);

  const resolveProductName = (reward: MissionReward) =>
    MissionDetailsTransformer.resolveProductName(reward, productDictionary);

  const resolveRewardType = (reward: MissionReward): MissionRewardType =>
    MissionDetailsTransformer.resolveRewardType(reward);

  const resolveRewardTypeLabel = (reward: MissionReward) =>
    MissionDetailsTransformer.resolveRewardTypeLabel(reward);

  const resolveConditionType = (condition: MissionCondition): string =>
    MissionDetailsTransformer.resolveConditionType(
      condition,
      providerDictionary,
    );

  const isOptionalValueCondition = (label?: string, providerKlass?: string) => {
    const normalizedType = normalizeText(label);
    const normalizedProvider = normalizeText(providerKlass);
    return OPTIONAL_QUANTITY_LABELS.some(
      (text) =>
        (normalizedType && normalizedType.includes(text)) ||
        (normalizedProvider && normalizedProvider.includes(text))
    );
  };

  const resolveConditionValue = (condition: MissionCondition): string => {
    if (
      isOptionalValueCondition(condition.type_label, condition.provider_klass)
    ) {
      return "--";
    }
    if (condition.properties?.value_label)
      return String(condition.properties.value_label);
    if (condition.properties?.value) return String(condition.properties.value);
    if (condition.value_label) return condition.value_label;
    return "--";
  };

  const missionName = mission.display_name || mission.name;

  return (
    <Box className="flex flex-col gap-8 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
      <div className="flex flex-col gap-4">
        <p className="text-xl font-bold mb-2">Detalhes da Missão</p>

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
          <div className="md:min-w-[200px]">
            <ViewField
              label="Tipo de missão"
              value={
                mission.mission_type
                  ? missionTypeStrategy[mission.mission_type as keyof typeof missionTypeStrategy]
                  : '--'
              }
            />
          </div>
          <div className="md:min-w-[200px]">
            <ViewField
              label="Status"
              value={mission.status === "active" ? "Ativa" : "Inativa"}
            />
          </div>
          {mission.mission_type === "establishment" && (
            <div className="md:flex-1">
              <ViewField
                label="Estabelecimento"
                value={mission.establishment?.display_name || "--"}
              />
            </div>
          )}
        </div>

        <ViewField label="Execução da missão">
          <ReadOnlyToggle
            label={
              mission.allow_multiple_times
                ? "Permite múltiplas execuções"
                : "Disponível uma única vez"
            }
            checked={mission.allow_multiple_times}
          />
          {mission.allow_multiple_times && (
            <span className="text-[13px] text-gray-300">
              {mission.max_times
                ? `${mission.max_times} vez(es) por usuário`
                : "Sem limite definido"}
            </span>
          )}
        </ViewField>

        <ViewField label="Nome" value={missionName} />
        <ViewField label="Descrição" value={mission.description} />

        <div className="grid grid-cols-2 gap-4">
          <ViewField label="Data inicial da missão">
            <Calendar size={18} className="text-gray-400" />
            <span>{mission.start_date}</span>
          </ViewField>
          <ViewField label="Data final da missão">
            <Calendar size={18} className="text-gray-400" />
            <span>{mission.end_date}</span>
          </ViewField>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-2">
        <p className="text-lg font-medium text-white mb-2">
          Requisitos da missão{" "}
          <span className="text-gray-400 text-sm font-normal">
            (Todos devem ser cumpridos para finalizar)
          </span>
        </p>
        <div className="flex flex-col gap-3">
          {mission.conditions.map((condition) => {
            const showValue = !isOptionalValueCondition(
              condition.type_label,
              condition.provider_klass
            );
            return (
              <CardItem key={condition.id}>
                <CardField
                  label="Tipo"
                  value={resolveConditionType(condition) || "--"}
                />
                {showValue && (
                  <CardField
                    label="Valor"
                    value={resolveConditionValue(condition) || "--"}
                  />
                )}
              </CardItem>
            );
          })}

          {mission.conditions.length === 0 && (
            <span className="text-sm text-gray-400">
              Nenhum requisito cadastrado.
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-2">
        <p className="text-xl font-bold text-white mb-2">Recompensas</p>
        <div className="flex flex-col gap-3">
          {mission.rewards.map((reward, index) => {
            const key = reward.id || `reward-${index}`;
            return (
              <CardItem key={key}>
                <CardField label="Tipo" value={resolveRewardTypeLabel(reward)} />
                {resolveRewardType(reward) === "product" && (
                  <CardField
                    className="md:col-span-2"
                    label="Produto"
                    value={resolveProductName(reward)}
                  />
                )}
                {resolveRewardType(reward) === "tickets" && (
                  <CardField
                    className="md:col-span-2"
                    label="Sorteio"
                    value={reward?.reward?.display_name}
                  />
                )}
                <CardField
                  className="md:col-span-1"
                  label="Quantidade"
                  value={reward.quantity ?? "--"}
                />
              </CardItem>
            );
          })}

          {mission.rewards.length === 0 && (
            <span className="text-sm text-gray-400">
              Nenhuma recompensa cadastrada.
            </span>
          )}
        </div>
      </div>
    </Box>
  );
};

